/**
 * Ticket status badges + operator messaging (T-030 / T-031).
 * Maps Case SRE gate/run status + decision reason codes to UI copy.
 */

import type { CaseSreInvestigationStatus } from '../types';

export type SreStatusTone =
  | 'neutral'
  | 'info'
  | 'warning'
  | 'danger'
  | 'success'
  | 'muted';

export interface SreStatusPresentation {
  /** Normalized status key (lowercase). */
  status: string;
  /** Short badge label. */
  label: string;
  /** Visible operator-facing explanation (T-031). */
  message: string | null;
  /** Longer tooltip / title text. */
  detail: string;
  tone: SreStatusTone;
  /** Pulse / animate the status indicator. */
  pulse: boolean;
  /** Show a visible message chip beside the badge. */
  showMessage: boolean;
}

const STATUS_LABELS: Record<string, string> = {
  pending_eval: 'Pending evaluation',
  skipped: 'Skipped',
  not_available: 'Not available',
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const STATUS_TONES: Record<string, SreStatusTone> = {
  pending_eval: 'warning',
  skipped: 'muted',
  not_available: 'warning',
  queued: 'info',
  running: 'info',
  completed: 'success',
  failed: 'danger',
  cancelled: 'muted',
};

const STATUS_DEFAULT_MESSAGES: Record<string, string> = {
  pending_eval: 'Waiting for the evaluation window before the SRE gate runs.',
  skipped: 'Investigation was skipped by policy or eligibility rules.',
  not_available:
    'No eligible use case or required skills — investigation cannot start yet.',
  queued: 'Investigation is queued until an execution slot is free.',
  running: 'An SRE investigation is in progress for this ticket.',
  completed: 'Latest SRE investigation finished successfully.',
  failed: 'The last SRE investigation failed. Review history or retry.',
  cancelled: 'The investigation was cancelled. You can start a new run when ready.',
};

/** Known evaluate / Pulse reason codes → operator copy. */
const REASON_MESSAGES: Record<string, string> = {
  policy_disabled: 'SRE investigation policy is disabled for this organization.',
  already_running: 'An investigation is already running or queued for this ticket.',
  cooldown:
    'A recent investigation decision is still in the cooldown window. Try again later or use reinvestigate when available.',
  transient_auto_resolve:
    'The alert self-cleared during the evaluation window, so no investigation was started.',
  no_matching_use_case:
    'No catalog use case matched this alert. Coverage gap — a new use case may need to be onboarded.',
  match_after_pulse:
    'No use case matched the inbound title. Pulse will load case details and select the playbook from that context.',
  missing_required_skills:
    'A matching use case exists, but required skills are missing or inactive.',
  evaluate_error: 'The eligibility evaluate call failed. The system will retry on the next schedule.',
  evaluate_error_persist_failed:
    'Evaluate failed and the ticket status could not be fully updated. Contact support if this persists.',
};

function humanizeToken(raw: string): string {
  const parts = raw.split('_').filter(Boolean);
  if (parts.length === 0) return raw;
  return parts
    .map((part, index) => {
      const lower = part.toLowerCase();
      if (index === 0) {
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      }
      return lower;
    })
    .join(' ');
}

/**
 * Map a machine reason code to operator-facing text.
 * Avoids regex on user-controlled strings (prefix / string checks only).
 */
export function formatSreDecisionReason(
  reason: string | null | undefined
): string | null {
  if (reason == null) return null;
  const trimmed = reason.trim();
  if (!trimmed) return null;

  const key = trimmed.toLowerCase();
  const known = REASON_MESSAGES[key];
  if (known) return known;

  const caseStatePrefix = 'case_state_';
  if (key.startsWith(caseStatePrefix)) {
    const state = trimmed.slice(caseStatePrefix.length).trim();
    if (!state) {
      return 'Investigation was stopped because of the current case state.';
    }
    return `Investigation was stopped because the case is in state “${humanizeToken(state)}”.`;
  }

  return humanizeToken(trimmed) + '.';
}

export function normalizeSreStatus(
  status?: CaseSreInvestigationStatus | string | null
): string {
  return (status || '').trim().toLowerCase();
}

/**
 * Build badge + messaging presentation for a case SRE status.
 */
export function getSreStatusPresentation(options: {
  status?: CaseSreInvestigationStatus | string | null;
  reason?: string | null;
  evaluatedAt?: string | null;
}): SreStatusPresentation | null {
  const status = normalizeSreStatus(options.status);
  if (!status || status === 'none') {
    return null;
  }

  const label = STATUS_LABELS[status] || humanizeToken(status);
  const tone = STATUS_TONES[status] || 'neutral';
  const reasonMessage = formatSreDecisionReason(options.reason);
  const defaultMessage = STATUS_DEFAULT_MESSAGES[status] || null;
  const message = reasonMessage || defaultMessage;

  const showMessage =
    Boolean(message) &&
    (status === 'skipped' ||
      status === 'not_available' ||
      status === 'failed' ||
      status === 'queued' ||
      status === 'pending_eval' ||
      status === 'running' ||
      status === 'cancelled');

  const detailParts = [
    `SRE investigation: ${label}`,
    reasonMessage
      ? `Reason: ${reasonMessage}`
      : options.reason
        ? `Reason: ${options.reason}`
        : null,
    options.evaluatedAt ? `Evaluated: ${options.evaluatedAt}` : null,
  ].filter(Boolean);

  return {
    status,
    label,
    message: showMessage ? message : null,
    detail: detailParts.join('\n'),
    tone,
    pulse: status === 'running' || status === 'pending_eval' || status === 'queued',
    showMessage,
  };
}

export const SRE_STATUS_TONE_STYLES: Record<
  SreStatusTone,
  { badge: string; message: string; dot: string }
> = {
  neutral: {
    badge: 'border-slate-200 bg-slate-50 text-slate-700',
    message: 'border-slate-200 bg-slate-50 text-slate-600',
    dot: 'bg-slate-500',
  },
  muted: {
    badge: 'border-slate-200 bg-slate-50 text-slate-600',
    message: 'border-slate-200 bg-slate-50 text-slate-600',
    dot: 'bg-slate-400',
  },
  info: {
    badge: 'border-sky-200 bg-sky-50 text-sky-800',
    message: 'border-sky-200 bg-sky-50 text-sky-800',
    dot: 'bg-sky-500',
  },
  warning: {
    badge: 'border-amber-200 bg-amber-50 text-amber-900',
    message: 'border-amber-200 bg-amber-50 text-amber-900',
    dot: 'bg-amber-500',
  },
  danger: {
    badge: 'border-red-200 bg-red-50 text-red-800',
    message: 'border-red-200 bg-red-50 text-red-800',
    dot: 'bg-red-500',
  },
  success: {
    badge: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    message: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    dot: 'bg-emerald-500',
  },
};
